import loadSystemLists from "@iewc/Store/Handlers/LoadSystemLists";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import loadQuoteIfNeeded from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/LoadQuoteIfNeeded";
import setExpirationDate from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetExpirationDate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { PageErrorWrapper } from "@insite/content-library/Components/PageErrorWrapper";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import { setGA4Event } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import { parseOrderType } from "@iewc/Utilities/Utilities";
/* Valtech Custom Code - End */

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ quoteId?: string }>(search);
    const quoteId = parsedQuery.quoteId;
    return {
        quoteId,
        quoteState: getQuoteState(state, quoteId),
    };
};

const mapDispatchToProps = {
    loadQuoteIfNeeded,
    setExpirationDate,

    /* Verndale Custom Feature - Request system list API */
    loadSystemLists,
};

export interface RfqQuoteDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
}

export const rfqQuoteDetailsPageStyles: RfqQuoteDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: { weight: "bold" },
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

const RfqQuoteDetailsPage = ({
    id,
    quoteId,
    quoteState,
    loadQuoteIfNeeded,
    setExpirationDate,

    /* Verndale Custom Feature - New methods */
    loadSystemLists,
    shellContext: { isInShell },
}: Props) => {
    // Valtech Custom Code
    const [viewQuoteSent, setViewQuoteSent] = React.useState(false);

    useEffect(() => {
        if (quoteId && !quoteState.isLoading && !quoteState.errorStatusCode) {
            loadQuoteIfNeeded({ quoteId });
        }
    }, [quoteId]);

    /* Valtech Custom Code - Start */
    useEffect(() => {
        if (!viewQuoteSent && quoteState.value && !quoteState.isLoading && quoteState.value.quoteLineCollection) {
            const quoteValue = quoteState.value;
            const { status } = quoteValue || {};
            const { orderChannel, itemsTotal } = quoteValue?.properties || {};
            const orderType = parseOrderType(orderChannel, true);
            const quantity = quoteValue?.quoteLineCollection?.length || 0;
            const value = Number(itemsTotal);
    
            setGA4Event([], "view_quote", { status, orderType, value, quantity });
            setViewQuoteSent(true);
        }
    }, [quoteState]);
    /* Valtech Custom Code - End */

    useEffect(() => {
        setExpirationDate({ expirationDate: undefined });

        /* Verndale Custom Feature - Call System Lists */
        if (!isInShell) {
            loadSystemLists({ namelist: "EditQuoteResponse,CancelQuoteResponse" });
        }
    }, []);

    const styles = rfqQuoteDetailsPageStyles;

    return (
        <Page>
            <PageErrorWrapper
                loadFailedTextTestSelector="rfqQuoteDetails_orderNotFound"
                value={quoteState}
                extendedStyles={styles}
                notFoundMessage={siteMessage("Rfq_OrderNotFound")}
            >
                <Zone contentId={id} zoneName="Content" />
            </PageErrorWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(RfqQuoteDetailsPage) as any) as any,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqQuoteDetailsPage" instead of this constant.
 */
export const RfqQuoteDetailsPageContext = "RfqQuoteDetailsPage";
